import React from 'react';
import { Col, DatePicker, Form, Input, Row, Switch } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { Parameter, ProductException, ProductExceptionIn } from '../api/types';
import { Mode } from '../store/types';
import {
  addFieldExternalIdToParameters,
  transformFormData,
} from '../utils/DataManagement';
import NewButton from './NewButton';
import ParameterInputs from './ParameterInputs';
import EvaluationExpressionInput from './EvaluationExpressionInput';

interface Props extends FormComponentProps<ProductExceptionIn> {
  data: ProductException;
  mode: Mode;
}

export const dateFormat = 'YYYY-MM-DD';

const PEForm: React.FC<Props> = ({
  form: { getFieldDecorator, getFieldValue, setFieldsValue },
  mode,
  data,
}) => {
  const isView = mode === Mode.VIEW;
  const { formatMessage } = useIntl();

  const parameters: Parameter[] = getFieldValue('parameters');

  const evaluationExpression = getFieldValue('evaluationExpression');
  const evaluationExpressionVp = getFieldValue('evaluationExpressionVp');

  const addParameter = () => {
    setFieldsValue({
      parameters: [...parameters, { id: '', name: '', externalId: '' }],
    });
  };
  getFieldDecorator('id');

  return (
    <Form
      labelCol={{ span: 10 }}
      wrapperCol={{ span: 14 }}
      className='pup-form'
    >
      <Row gutter={16} type='flex' justify='space-around' align='middle'>
        <Col span={9}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.productId' />
            }
          >
            {getFieldDecorator('productId', {})(<Input disabled={isView} />)}
          </Form.Item>
        </Col>
        <Col span={4} offset={11}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.active' />
            }
          >
            {getFieldDecorator('active', {
              valuePropName: 'checked',
            })(<Switch disabled={isView} />)}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={9}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.ean' />
            }
          >
            {getFieldDecorator('ean', {})(<Input disabled={isView} />)}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={9}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.eval_expression' />
            }
          >
            {getFieldDecorator('evaluationExpression', {
              rules: [
                {
                  required: !evaluationExpressionVp,
                  message: <FormattedMessage id='validations.required' />,
                },
              ],
            })(<EvaluationExpressionInput disabled={isView} />)}
          </Form.Item>
        </Col>
        <Col span={6} offset={2}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.valid_from' />
            }
          >
            {getFieldDecorator('validFromM', {
              initialValue:
                data.validFrom != null &&
                data.validFrom &&
                data.validFrom !== ''
                  ? moment(data.validFrom, dateFormat)
                  : null,
              rules: [
                {
                  required: !!evaluationExpression,
                  message: <FormattedMessage id='validations.required' />,
                },
              ],
            })(
              <DatePicker
                format={dateFormat}
                disabled={isView}
                placeholder={formatMessage({ id: 'common.date_mask' })}
              />,
            )}
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.valid_to' />
            }
          >
            {getFieldDecorator('validToM', {
              initialValue:
                data.validTo != null && data.validTo && data.validTo !== ''
                  ? moment(data.validTo, dateFormat)
                  : null,
            })(
              <DatePicker
                format={dateFormat}
                disabled={isView}
                placeholder={formatMessage({ id: 'common.date_mask' })}
              />,
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={9}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.eval_expression_vp' />
            }
          >
            {getFieldDecorator('evaluationExpressionVp', {
              rules: [
                {
                  required: !evaluationExpression,
                  message: <FormattedMessage id='validations.required' />,
                },
              ],
            })(<EvaluationExpressionInput disabled={isView} />)}
          </Form.Item>
        </Col>
        <Col span={6} offset={2}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.valid_from_vp' />
            }
          >
            {getFieldDecorator('validFromVpM', {
              initialValue:
                data.validFromVp != null &&
                data.validFromVp &&
                data.validFromVp !== ''
                  ? moment(data.validFromVp, dateFormat)
                  : null,
              rules: [
                {
                  required: !!evaluationExpressionVp,
                  message: <FormattedMessage id='validations.required' />,
                },
              ],
            })(
              <DatePicker
                format={dateFormat}
                disabled={isView}
                placeholder={formatMessage({ id: 'common.date_mask' })}
              />,
            )}
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label={
              <FormattedMessage id='product_exceptions.table.titles.valid_to_vp' />
            }
          >
            {getFieldDecorator('validToVpM', {
              initialValue:
                data.validToVp != null &&
                data.validToVp &&
                data.validToVp !== ''
                  ? moment(data.validToVp, dateFormat)
                  : null,
            })(
              <DatePicker
                format={dateFormat}
                disabled={isView}
                placeholder={formatMessage({ id: 'common.date_mask' })}
              />,
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} type='flex' justify='space-around' align='middle'>
        <Col span={32} className='params-title'>
          <Form.Item>
            <div className='ant-form-text ant-modal-title'>
              <FormattedMessage id='product_exceptions.table.titles.parameters' />
            </div>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16} justify='space-around' align='middle'>
        <Col span={32}>
          {!isView && <NewButton onClick={addParameter} />}
          {getFieldDecorator('parameters')(
            <ParameterInputs disabled={isView} />,
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default Form.create<Props>({
  mapPropsToFields: ({
    data: { validFrom, validTo, validFromVp, validToVp, parameters, ...rest },
  }) =>
    transformFormData({
      validFromM:
        validFrom != null && validFrom && validFrom !== ''
          ? moment(validFrom, dateFormat)
          : null,
      validToM:
        validTo != null && validTo && validTo !== ''
          ? moment(validTo, dateFormat)
          : null,
      validFromVpM:
        validFromVp != null && validFromVp && validFromVp !== ''
          ? moment(validFromVp, dateFormat)
          : null,
      validToVpM:
        validToVp != null && validToVp && validToVp !== ''
          ? moment(validToVp, dateFormat)
          : null,
      parameters: addFieldExternalIdToParameters(parameters),
      ...rest,
    }),
})(PEForm);
